import type { ZudokuConfig } from "zudoku";

const config: ZudokuConfig = {
  metadata: {
    title: "Gesturcit API Docs",
    description:
      "Documentacion tecnica de Gesturcit para integraciones, autenticacion y operativa de citas y turnos.",
    applicationName: "Gesturcit API Docs",
  },
  site: {
    title: "API Docs",
    logo: {
      src: {
        light: "/gesturcit_logo_color-1-1-300x99.png",
        dark: "/gesturcit_logo_color-1-1-300x99.png",
      },
      alt: "Gesturcit",
      width: "164px",
      href: "/introduction",
      reloadDocument: false,
    },
    footer: {
      position: "center",
      logo: {
        src: {
          light: "/gesturcit_logo_color-1-1-300x99.png",
          dark: "/gesturcit_logo_color-1-1-300x99.png",
        },
        alt: "Gesturcit",
        width: "164px",
      },
      columns: [
        {
          title: "Producto",
          links: [
            { label: "Documentacion", href: "/introduction" },
            { label: "Referencia API", href: "/api" },
            { label: "Sitio web", href: "https://gesturcit.com/" },
          ],
        },
        {
          title: "Recursos",
          links: [
            { label: "Solicitar demo", href: "https://gesturcit.com/" },
            { label: "API REST", href: "/api" },
            { label: "Contacto", href: "mailto:info@gesturcit.com" },
          ],
        },
      ],
      social: [
        {
          icon: "linkedin",
          href: "https://www.linkedin.com/company/gesturcit/",
          label: "LinkedIn",
        },
      ],
      copyright: `© ${new Date().getFullYear()} Gesturcit. Todos los derechos reservados.`,
    },
  },
  header: {
    placements: {
      navigation: "start",
      search: "end",
    },
  },
  docs: {
    files: "/pages/**/*.{md,mdx}",
  },
  syntaxHighlighting: {
    languages: ["http"],
  },
  theme: {
    fonts: {
      sans: {
        url: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Poppins:wght@600;700;800&display=swap",
        fontFamily: "Inter, sans-serif",
      },
      mono: "IBM Plex Mono",
    },
    light: {
      background: "#FFFFFF",
      foreground: "#111827",
      card: "#FFFFFF",
      cardForeground: "#111827",
      popover: "#FFFFFF",
      popoverForeground: "#111827",
      primary: "#8638E5",
      primaryForeground: "#FFFFFF",
      secondary: "#F5F6FA",
      secondaryForeground: "#111827",
      muted: "#F5F6FA",
      mutedForeground: "#4B5563",
      accent: "#F3EEFF",
      accentForeground: "#602AA3",
      destructive: "#D64747",
      destructiveForeground: "#FFFFFF",
      border: "#E5E7EB",
      input: "#E5E7EB",
      ring: "#7165E9",
      radius: "1rem",
    },
    dark: {
      background: "#0E1020",
      foreground: "#F8F8FF",
      card: "#17192D",
      cardForeground: "#F8F8FF",
      popover: "#17192D",
      popoverForeground: "#F8F8FF",
      primary: "#A06AF5",
      primaryForeground: "#FFFFFF",
      secondary: "#1F2138",
      secondaryForeground: "#F8F8FF",
      muted: "#1F2138",
      mutedForeground: "#B5B8CB",
      accent: "#261F46",
      accentForeground: "#F8F8FF",
      destructive: "#FF6A6A",
      destructiveForeground: "#FFFFFF",
      border: "#2B2D46",
      input: "#2B2D46",
      ring: "#A06AF5",
      radius: "1rem",
    },
    customCss: `
      :root {
        --gesturcit-purple: #8638E5;
        --gesturcit-purple-hover: #7933CF;
      }

      body {
        background:
          radial-gradient(circle at top left, rgba(120, 140, 244, 0.10), transparent 28%),
          radial-gradient(circle at top right, rgba(134, 56, 229, 0.08), transparent 22%),
          var(--background);
      }

      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        font-family: "Poppins", sans-serif;
        letter-spacing: -0.02em;
      }

      a {
        transition: color 0.2s ease;
      }

      main a:not([class]) {
        color: var(--gesturcit-purple);
      }

      main a:not([class]):hover {
        color: var(--gesturcit-purple-hover);
      }

      [data-slot="top-navigation"],
      header {
        backdrop-filter: blur(18px);
      }
    `,
  },
  navigation: [
    {
      type: "category",
      label: "Plataforma",
      icon: "building-2",
      items: [
        {
          type: "doc",
          file: "introduction",
          label: "Vision general",
          icon: "sparkles",
        },
        {
          type: "doc",
          file: "getting-started",
          label: "Primeros pasos",
          icon: "rocket",
        },
        {
          type: "doc",
          file: "manual-usuario",
          label: "Manual de usuario",
          icon: "book-open",
        },
        {
          type: "doc",
          file: "authentication",
          label: "Autenticacion",
          icon: "shield-check",
        },
      ],
    },
    {
      type: "link",
      to: "/api",
      label: "API REST",
      icon: "code-xml",
      badge: {
        label: "v1",
        color: "indigo",
      },
    },
    {
      type: "category",
      label: "Enlaces",
      icon: "link",
      items: [
        {
          type: "link",
          label: "Sitio corporativo",
          to: "https://gesturcit.com/",
          icon: "globe",
        },
        {
          type: "link",
          label: "Solicitar demo",
          to: "https://gesturcit.com/",
          icon: "calendar-plus",
        },
      ],
    },
  ],
  redirects: [{ from: "/", to: "/introduction" }],
  apis: [
    {
      type: "file",
      input: "./apis/openapi.yaml",
      path: "/api",
    },
  ],
};

export default config;
